import * as React from 'react';

import { LucidePlus } from 'lucide-react';
import MiniSearch from 'minisearch';

export const useComboBoxOptions = ({
  options,
  maxResults,
  newItemLabel,
  value,
  disabled,
}: {
  value: string;
  options: {
    id: string;
    label: string;
    searchableContent?: string;
  }[];
  newItemLabel?: React.ReactNode;
  maxResults?: number;
  disabled?: boolean;
}) => {
  const [query, setQuery] = React.useState('');

  const searchable = React.useMemo(() => {
    const miniSearch = new MiniSearch({
      fields: ['label', 'subLabel', 'searchableContent'],
      storeFields: ['id', 'item'],
      searchOptions: {
        prefix: true,
        fuzzy: 0.2,
      },
    });

    if (!disabled) {
      miniSearch.addAll(options.map((it) => ({ ...it, item: it })));
    }

    return miniSearch;
  }, [options, disabled]);

  const filteredOptions = React.useMemo(() => {
    if (disabled) return [];

    const filtered = [
      ...(newItemLabel
        ? [
            {
              id: 'new',
              label: (
                <span className="flex items-center gap-2">
                  <LucidePlus className="size-[18px] opacity-70" />
                  {newItemLabel}
                </span>
              ),
            },
          ]
        : []),
      ...(query
        ? searchable.search(query).map((it) => it.item)
        : options
      ).slice(0, maxResults),
    ];

    return maxResults && !filtered.some((it) => it.id === value)
      ? [...filtered, options.find((it) => it.id === value)].filter(Boolean)
      : filtered;
  }, [disabled, newItemLabel, query, searchable, options, maxResults, value]);

  const currentOption = React.useMemo(
    () => filteredOptions.find((it) => it.id === value),
    [filteredOptions, value],
  );

  return {
    query,
    onChangeQuery: setQuery,
    filteredOptions,
    currentOption,
  };
};
